import React, { useEffect, Fragment, useState } from "react";
import { func, any, bool, string } from "prop-types";
import { connect } from "react-redux";
import * as style from "./styles.module.scss";
import Booking from "../Booking";
import { splitBookings } from "../../helpers/helpers";
import { bookingsRequestData, clearErrors } from "../../redux/actions";
import { Button } from "../../common";
import SearchBox from "../SearchBox";
import BookingsLoading from "../BookingsLoading";
import { BookingStatus } from "../../helpers/bookings";
import BookingsLoadingError from "../BookingsLoadingError";

const noEndDateValue = "0001-01-01T00:00:00.0000000+00:00";

/* 
  Example
  Input: 
    2020-03-01T00:00:00.0000000+00:00
  Output:
    01/03/20
*/
const parseDate = (date) =>
  date === noEndDateValue
    ? "No end date"
    : date.substr(2, 8).replace(/-/g, "/").split("/").reverse().join("/");

const BookingsList = ({
  searchValue,
  toggleShowPastBookings,
  dividedBookings,
  isLoading,
  showPastBookings,
  filteredBookings,
}) => {
  return searchValue === "" ? (
    <Fragment>
      <div className={style.bookings__wrapper}>
        {dividedBookings ? (
          dividedBookings.currentBookings.map((_booking, _index) => (
            <Booking
              key={`curr${_booking.jobName}${String(_index)}`}
              {..._booking}
              parseDate={parseDate}
            />
          ))
        ) : (
          <BookingsLoading />
        )}
      </div>
      <div
        className={dividedBookings && !isLoading ? "d-block mb-2" : "d-none"}
      >
        <Button
          onClick={toggleShowPastBookings}
          classes={`m-auto btn btn-block ${style.bookings__showPast}`}
        >
          {showPastBookings ? "Hide my past bookings" : "Show my past bookings"}
        </Button>
        <div className={style.bookings__wrapper}>
          {showPastBookings &&
            dividedBookings &&
            dividedBookings.pastBookings.map((_booking, _index) => (
              <Booking
                key={`past${_booking.jobName}${String(_index)}`}
                {..._booking}
                parseDate={parseDate}
                isPast
              />
            ))}
        </div>
      </div>
    </Fragment>
  ) : (
    <Fragment>
      {filteredBookings && filteredBookings.length !== 0 ? (
        <div className={style.bookings__wrapper}>
          {filteredBookings.map((_booking, _index) => (
            <Booking
              key={`search${_booking.jobName}${String(_index)}`}
              {..._booking}
              parseDate={parseDate}
            />
          ))}
        </div>
      ) : (
        <article className="d-flex flex-column align-items-center justify-content-center">
          <h4>Sorry, we couldn&apos;t find any matches for</h4>
          <h4 className={style.bookings__emptySearch}>{searchValue}</h4>
          <p>Please try searching with another term.</p>
        </article>
      )}
    </Fragment>
  );
};

BookingsList.propTypes = {
  searchValue: string,
  toggleShowPastBookings: func,
  dividedBookings: any,
  isLoading: bool,
  showPastBookings: bool,
  filteredBookings: any,
};

const Bookings = ({
  requestBookingsData,
  bookings = undefined,
  isLoading,
  errors,
  clearErrorsWithSource,
}) => {
  useEffect(() => {
    if (!isLoading && bookings === undefined) {
      requestBookingsData();
    }
  }, []);
  const [showPastBookings, setShowPastBookings] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [bookingStatus, setBookingStatus] = useState(BookingStatus.Loading);

  const toggleShowPastBookings = () => setShowPastBookings(!showPastBookings);

  const filteredBookings =
    bookings &&
    searchValue !== "" &&
    bookings.filter((_booking) =>
      _booking.jobTitle.toLowerCase().includes(searchValue.toLowerCase())
    );
  const dividedBookings = bookings && splitBookings(bookings);
  const bookingsErrors = errors.filter((error) => error.source === "bookings");
  const isError = () =>
    !isLoading && bookingsErrors && bookingsErrors.length > 0;
  const isValid = () => !isLoading && bookings;

  useEffect(() => {
    if (isValid()) {
      setBookingStatus(BookingStatus.Success);
    }
    if (isError()) {
      setBookingStatus(BookingStatus.Error);
    }
  }, [bookings, isLoading]);

  const handleRetry = () => {
    setBookingStatus(BookingStatus.Loading);
    clearErrorsWithSource("bookings");
    requestBookingsData();
  };

  switch (bookingStatus) {
    case BookingStatus.Loading:
      return (
        <BookingsLoading
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          isLoading={isLoading}
        />
      );
    case BookingStatus.Success:
      return (
        <div>
          <h2>Your Bookings</h2>
          <SearchBox
            value={searchValue}
            onChange={setSearchValue}
            placeholder="Search"
            onCloseClick={() => setSearchValue("")}
            disabled={isLoading}
          />
          <div
            className={`${style.bookings__searchAmount} ${
              searchValue === "" ? "invisible" : ""
            }`}
          >
            <b>{`${
              filteredBookings && filteredBookings.length
            } result(s) found`}</b>
          </div>
          {bookings && !isLoading && bookings.length === 0 ? (
            <article>
              <h4>There are no bookings listed yet.</h4>
              <p>You’ll be able to review your booking details from here.</p>
              <p>
                If you believe there are bookings missing, please contact your
                HRGO consultant.
              </p>
            </article>
          ) : (
            <div>
              <BookingsList
                searchValue={searchValue}
                toggleShowPastBookings={toggleShowPastBookings}
                dividedBookings={dividedBookings}
                isLoading={isLoading}
                showPastBookings={showPastBookings}
                filteredBookings={filteredBookings}
                errors={errors}
                clearErrorsWithSource={clearErrorsWithSource}
              />
            </div>
          )}
        </div>
      );
    case BookingStatus.Error:
      return <BookingsLoadingError onClick={handleRetry} />;
    default:
      return <Fragment></Fragment>;
  }
};

Bookings.propTypes = {
  requestBookingsData: func,
  bookings: any,
  isLoading: bool,
  errors: any,
  clearErrorsWithSource: func,
};

const mapStateToProps = ({
  core: {
    bookings: { isLoading, bookings },
    errors,
  },
}) => ({
  isLoading,
  bookings,
  errors,
});

const mapDispatchToProps = {
  requestBookingsData: bookingsRequestData,
  clearErrorsWithSource: clearErrors,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(Bookings);
